import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Eye, Pencil } from "lucide-react";
import { productAPI, pdirAPI } from "../../services/api";

export default function PdirTemplate() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [date, setDate] = useState("");
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    productAPI.list().then((r) => setProducts(r.data || []));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: pageSize, offset: (page - 1) * pageSize };
    if (status) params.status = status;
    if (search.trim()) params.search = search.trim();
    if (date) params.date = date;
    pdirAPI
      .listReports(params)
      .then((r) => {
        setReports(r.data.reports || []);
        setTotal(r.data.total || 0);
      })
      .finally(() => setLoading(false));
  }, [status, search, date, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [status, search, date]);

  const finalize = async (id) => {
    if (
      !window.confirm(
        "Finalize this PDIR report? Once finalized it can NEVER be edited again, by anyone.",
      )
    )
      return;
    await pdirAPI.finalizeReport(id);
    load();
  };

  return (
    <div style={{ padding: 28 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>PDIR Template</h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            Pre-Despatch Inspection Reports
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => navigate("/company/pdir/new")}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={14} /> Add PDIR
        </button>
      </div>

      <div
        className="card"
        style={{
          marginBottom: 20,
          display: "flex",
          flexWrap: "wrap",
          gap: 14,
          alignItems: "flex-end",
        }}
      >
        <div
          className="form-group"
          style={{ minWidth: 240, flex: "1 1 240px" }}
        >
          <label>Search (Product ID / Name)</label>
          <input
            type="text"
            placeholder="e.g. P001 or Part1"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="form-group" style={{ minWidth: 160 }}>
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Final">Final</option>
          </select>
        </div>
        <div className="form-group">
          <label>Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        {(search || date || status) && (
          <button
            className="btn-secondary"
            onClick={() => {
              setSearch("");
              setDate("");
              setStatus("");
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div
            style={{ padding: 24, textAlign: "center", color: "var(--text2)" }}
          >
            Loading...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Part</th>
                <th>INV No.</th>
                <th>Qty</th>
                <th>Cavities</th>
                <th>Status</th>
                <th>Checked By</th>
                <th>Approved By</th>
                <th style={{ width: 90 }}>View</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      textAlign: "center",
                      padding: 20,
                      color: "var(--text3)",
                    }}
                  >
                    No PDIR reports found
                  </td>
                </tr>
              )}
              {reports.map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.date).toLocaleDateString()}</td>
                  <td style={{ fontWeight: 500 }}>
                    {r.product_name}{" "}
                    <span className="text3 mono" style={{ fontSize: 11 }}>
                      ({r.product_code})
                    </span>
                  </td>
                  <td className="mono">{r.inv_no || "—"}</td>
                  <td>{r.qty ?? "—"}</td>
                  <td>{r.no_of_cavities ?? "—"}</td>
                  <td>
                    <select
                      value={r.status}
                      disabled={r.status === "Final"}
                      onChange={(e) => {
                        if (e.target.value === "Final") finalize(r.id);
                      }}
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 6,
                        background:
                          r.status === "Final"
                            ? "rgba(0,224,150,0.12)"
                            : "rgba(255,77,106,0.12)",
                        color:
                          r.status === "Final"
                            ? "var(--success)"
                            : "var(--danger)",
                        border: `1px solid ${r.status === "Final" ? "var(--success)" : "var(--danger)"}`,
                        cursor:
                          r.status === "Final" ? "not-allowed" : "pointer",
                      }}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Final">Final</option>
                    </select>
                  </td>
                  <td className="text2">{r.checked_by || "—"}</td>
                  <td className="text2">{r.approved_by || "—"}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        title="View"
                        onClick={() => navigate(`/company/pdir/${r.id}`)}
                        style={iconBtn}
                      >
                        <Eye size={13} />
                      </button>
                      {r.status === "Draft" && (
                        <button
                          title="Edit"
                          onClick={() => navigate(`/company/pdir/${r.id}/edit`)}
                          style={iconBtn}
                        >
                          <Pencil size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > pageSize && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 14,
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--text3)" }}>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of{" "}
            {total}
          </span>
          <button
            className="btn-secondary"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </button>
          <button
            className="btn-secondary"
            disabled={page * pageSize >= total}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  background: "var(--surface2)",
  border: "1px solid var(--border2)",
  borderRadius: 6,
  padding: 5,
  cursor: "pointer",
  color: "var(--text2)",
  display: "flex",
};
